import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { TextField, Button, Paper, Typography, Box, Alert, CircularProgress } from '@mui/material';
import { db } from '../../firebase';
import { collection, addDoc, getDoc, doc } from 'firebase/firestore';
import Logo from '../../assets/logo.png';

interface Event {
  id?: string;
  name: string;
  date: string;
  description: string;
  active: boolean;
}

const EventForm: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    linkedin: '',
    country: '',
    speciality: '',
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error' | 'submitting'>('idle');

  useEffect(() => {
    const fetchEvent = async () => {
      setLoading(true);
      setError('');
      try {
        if (!eventId) throw new Error('No event ID provided');
        const eventDoc = await getDoc(doc(db, 'events', eventId));
        if (!eventDoc.exists()) throw new Error('Event not found');
        setEvent({ id: eventDoc.id, ...eventDoc.data() } as Event);
      } catch (err: any) {
        setError(err.message || 'Failed to load event');
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (event && !event.active) {
      setSubmitStatus('error');
      setError('This event is not accepting responses at this time.');
      return;
    }
    setSubmitStatus('submitting');
    try {
      if (!eventId) throw new Error('No event ID');
      await addDoc(collection(db, 'events', eventId, 'responses'), {
        ...form,
        submittedAt: new Date(),
      });
      setSubmitStatus('success');
      setForm({ name: '', email: '', linkedin: '', country: '', speciality: '' });
    } catch (err) {
      setSubmitStatus('error');
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px"><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!event) return null;
  if (!event.active) return (
    <Box maxWidth={500} mx="auto" mt={4}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>{event.name}</Typography>
        <Typography variant="subtitle1" gutterBottom>{event.date}</Typography>
        <Typography variant="body1" gutterBottom>{event.description}</Typography>
        <Alert severity="warning" sx={{ mt: 3 }}>
          This event is not accepting responses at this time.
        </Alert>
      </Paper>
    </Box>
  );

  return (
    <Box maxWidth={500} mx="auto" mt={4}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <img src={Logo} alt="SyrDevUae Logo" style={{ width: 120, marginBottom: 24, display: 'block', marginLeft: 'auto', marginRight: 'auto' }} />
        <Typography variant="h5" gutterBottom>{event.name}</Typography>
        <Typography variant="subtitle1" gutterBottom>{event.date}</Typography>
        <Typography variant="body1" gutterBottom>{event.description}</Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Name" name="name" value={form.name} onChange={handleChange} required />
          <TextField label="Email" name="email" value={form.email} onChange={handleChange} required type="email" />
          <TextField label="LinkedIn" name="linkedin" value={form.linkedin} onChange={handleChange} />
          <TextField label="Country of Residence" name="country" value={form.country} onChange={handleChange} required />
          <TextField label="Speciality" name="speciality" value={form.speciality} onChange={handleChange} required />
          <Button type="submit" variant="contained" color="primary" disabled={submitStatus === 'submitting'}>
            {submitStatus === 'submitting' ? 'Submitting...' : 'Submit'}
          </Button>
          {submitStatus === 'success' && <Alert severity="success">Form submitted successfully!</Alert>}
          {submitStatus === 'error' && <Alert severity="error">Failed to submit. Please try again.</Alert>}
        </Box>
      </Paper>
    </Box>
  );
};

export default EventForm; 